/**
 * Repeat picker: a structured face over `spec.recurrence` (an RRULE string).
 * Covers the everyday shapes as chips (daily, weekdays, weekly on days, monthly
 * on a date, yearly) with an interval, and falls back to the raw rule for
 * anything else so nothing the server accepts becomes uneditable.
 */
import { useState } from 'react'
import { RRule, rrulestr } from 'rrule'
import { cx } from './ui'

type Mode = 'never' | 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'yearly' | 'custom'

const DAYS = [
  { code: 'MO', label: 'M' },
  { code: 'TU', label: 'T' },
  { code: 'WE', label: 'W' },
  { code: 'TH', label: 'T' },
  { code: 'FR', label: 'F' },
  { code: 'SA', label: 'S' },
  { code: 'SU', label: 'S' },
] as const
const WEEKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR']
const MODES: { value: Mode; label: string }[] = [
  { value: 'never', label: 'Never' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'custom', label: 'Custom' },
]

export type Shape = { mode: Mode; interval: number; days: string[]; monthDay: number; raw: string }

const dayCode = (n: number) => DAYS[n]?.code ?? 'MO'

/** Read an RRULE into chips; anything the chips can't express is `custom`. */
export const readRule = (rule: string | undefined): Shape => {
  const raw = (rule ?? '').trim()
  const base: Shape = { mode: 'never', interval: 1, days: ['MO'], monthDay: 1, raw }
  if (!raw) return base
  let r: RRule
  try {
    r = rrulestr(raw.includes('RRULE:') ? raw : `RRULE:${raw}`) as RRule
  } catch {
    return { ...base, mode: 'custom' }
  }
  const o = r.origOptions
  const interval = o.interval ?? 1
  const extras = [
    o.count,
    o.until,
    o.bymonth,
    o.byhour,
    o.byminute,
    o.bysetpos,
    o.byweekno,
    o.byyearday,
  ].some((v) => v !== undefined && v !== null && !(Array.isArray(v) && v.length === 0))
  if (extras) return { ...base, mode: 'custom' }
  const byday = (Array.isArray(o.byweekday) ? o.byweekday : o.byweekday ? [o.byweekday] : []).map((d) =>
    typeof d === 'number' ? dayCode(d) : d.toString(),
  )
  const byMonthDay = Array.isArray(o.bymonthday) ? o.bymonthday : o.bymonthday ? [o.bymonthday] : []
  if (byday.some((d) => /\d/.test(d))) return { ...base, mode: 'custom' } // e.g. 2MO (second Monday)
  switch (o.freq) {
    case RRule.DAILY:
      return byday.length || byMonthDay.length
        ? { ...base, mode: 'custom' }
        : { ...base, mode: 'daily', interval }
    case RRule.WEEKLY: {
      if (byMonthDay.length) return { ...base, mode: 'custom' }
      const days = byday.length ? byday : ['MO']
      if (interval === 1 && days.length === 5 && WEEKDAYS.every((d) => days.includes(d)))
        return { ...base, mode: 'weekdays', days }
      return { ...base, mode: 'weekly', interval, days }
    }
    case RRule.MONTHLY:
      if (byday.length || byMonthDay.length > 1) return { ...base, mode: 'custom' }
      return { ...base, mode: 'monthly', interval, monthDay: byMonthDay[0] ?? 1 }
    case RRule.YEARLY:
      return byday.length || byMonthDay.length
        ? { ...base, mode: 'custom' }
        : { ...base, mode: 'yearly', interval }
    default:
      return { ...base, mode: 'custom' }
  }
}

/** Chips back to an RRULE string ('' for never). */
export const writeRule = (s: Shape): string => {
  const every = s.interval > 1 ? `;INTERVAL=${s.interval}` : ''
  switch (s.mode) {
    case 'never':
      return ''
    case 'daily':
      return `FREQ=DAILY${every}`
    case 'weekdays':
      return 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR'
    case 'weekly':
      return `FREQ=WEEKLY${every};BYDAY=${(s.days.length ? s.days : ['MO']).join(',')}`
    case 'monthly':
      return `FREQ=MONTHLY${every};BYMONTHDAY=${s.monthDay}`
    case 'yearly':
      return `FREQ=YEARLY${every}`
    case 'custom':
      return s.raw
  }
}

/** Plain-English reading of a rule, or the reason it does not parse. */
export const describeRule = (rule: string | undefined): string => {
  const raw = (rule ?? '').trim()
  if (!raw) return 'Does not repeat'
  try {
    const text = rrulestr(raw.includes('RRULE:') ? raw : `RRULE:${raw}`).toText()
    return text.charAt(0).toUpperCase() + text.slice(1)
  } catch {
    return 'Not a valid rule yet'
  }
}

const unit = (mode: Mode, n: number) =>
  ({ daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year' })[mode as 'daily'] + (n > 1 ? 's' : '')

const chip = (on: boolean) =>
  cx(
    'rounded-full px-3 min-h-8 text-xs font-medium transition-colors border',
    on ? 'bg-accent text-on-accent border-accent' : 'bg-raised text-muted border-line hover:text-fg',
  )

export const RepeatPicker = ({
  value,
  onChange,
}: {
  value: string | undefined
  onChange: (rule: string) => void
}) => {
  // chips are local state seeded from the value; only complete rules are pushed up
  const [shape, setShape] = useState<Shape>(() => readRule(value))
  const update = (patch: Partial<Shape>) => {
    const next = { ...shape, ...patch }
    setShape(next)
    onChange(writeRule(next))
  }
  const hasInterval =
    shape.mode === 'daily' || shape.mode === 'weekly' || shape.mode === 'monthly' || shape.mode === 'yearly'
  return (
    <div data-testid="repeat-picker">
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Repeat">
        {MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            role="radio"
            aria-checked={shape.mode === m.value}
            className={chip(shape.mode === m.value)}
            onClick={() =>
              update({
                mode: m.value,
                raw: m.value === 'custom' ? writeRule({ ...shape, mode: shape.mode }) : shape.raw,
              })
            }
            data-testid={`repeat-${m.value}`}
          >
            {m.label}
          </button>
        ))}
      </div>
      {shape.mode === 'weekly' && (
        <div className="flex gap-1.5 mt-2" role="group" aria-label="Days">
          {DAYS.map((d) => {
            const on = shape.days.includes(d.code)
            return (
              <button
                key={d.code}
                type="button"
                aria-pressed={on}
                aria-label={d.code}
                className={cx(chip(on), 'w-8 px-0 justify-center')}
                onClick={() =>
                  update({
                    days: on
                      ? shape.days.length > 1
                        ? shape.days.filter((x) => x !== d.code)
                        : shape.days
                      : DAYS.map((x) => x.code).filter((c) => c === d.code || shape.days.includes(c)),
                  })
                }
                data-testid={`repeat-day-${d.code}`}
              >
                {d.label}
              </button>
            )
          })}
        </div>
      )}
      {(hasInterval || shape.mode === 'monthly') && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-2 text-sm text-muted">
          {hasInterval && (
            <span className="flex items-center gap-2">
              every
              <input
                type="number"
                min={1}
                max={99}
                value={shape.interval}
                onChange={(e) => update({ interval: Math.max(1, Math.min(99, Number(e.target.value) || 1)) })}
                className="w-16 py-1.5 px-2 text-sm"
                aria-label="Interval"
              />
              {unit(shape.mode, shape.interval)}
            </span>
          )}
          {shape.mode === 'monthly' && (
            <span className="flex items-center gap-2">
              on day
              <input
                type="number"
                min={1}
                max={31}
                value={shape.monthDay}
                onChange={(e) => update({ monthDay: Math.max(1, Math.min(31, Number(e.target.value) || 1)) })}
                className="w-16 py-1.5 px-2 text-sm"
                aria-label="Day of month"
              />
            </span>
          )}
        </div>
      )}
      {shape.mode === 'custom' && (
        <input
          value={shape.raw}
          onChange={(e) => update({ raw: e.target.value })}
          className="w-full font-mono text-sm mt-2"
          placeholder="FREQ=MONTHLY;BYDAY=2MO"
          aria-label="RRULE"
          data-testid="repeat-raw"
        />
      )}
      <p className="text-xs text-faint mt-1.5" data-testid="repeat-summary">
        {describeRule(writeRule(shape))}
      </p>
    </div>
  )
}
